import ImageModal from "components/ImageModal";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import AppealRequestForm from "pages/InstituteOfCharge/modal/AppealRequestForm";
import DefendantAppealRequestForm from "pages/InstituteOfCharge/modal/DefendantAppealRequestForm";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AmmendedCourtDecisionType,
  AppealDailyReport,
  AppealDailyReportStatus,
  AppealFinalVerdict,
  AppealPreJudgement,
  AppealRequest,
} from "types";
import AppealAddDailyReport from "./fragment/AppealAddDailyReport";
import AppealAddFinalVerdict from "./fragment/AppealAddFinalVerdict";
import AppealApplicationTab from "./fragment/AppealApplicationTab";
import AppealPreJudgmentForm from "./fragment/AppealPreJudgmentForm";
import FinalVerdictDecisionUpload from "./fragment/FinalVerdictDecisionUpload";

type Props = {
  pre_judgement_entries?: AppealPreJudgement[];
  daily_report?: AppealDailyReport[];
  appeal_final_verdict?: AppealFinalVerdict[];
  setSelectedAppealDailyReport?: React.Dispatch<
    React.SetStateAction<AppealDailyReport | undefined>
  >;
  selectedAppealDailyReport?: AppealDailyReport;
  isProsecutor?: boolean;
  isRegistrar?: boolean;
  isCoordinator?: boolean;
  appealData: AppealRequest;
  fetchData: () => void;
};

// Reusable Card Component
const ReportCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  selected?: boolean;
}> = ({ children, className = "", onClick, selected = false }) => (
  <div
    className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 transition-all duration-200 hover:shadow-md ${
      selected ? "ring-2 ring-red-500 border-red-300" : ""
    } ${onClick ? "cursor-pointer" : ""} ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
);

// Empty State Component
const EmptyState: React.FC<{
  title: string;
  description: string;
  icon?: React.ReactNode;
}> = ({ title, description, icon }) => (
  <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border border-gray-200">
    {icon || (
      <svg
        className="w-16 h-16 text-gray-300 mb-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
        />
      </svg>
    )}
    <p className="text-gray-500 text-lg font-medium mb-2">{title}</p>
    <p className="text-gray-400 text-sm text-center max-w-md">{description}</p>
  </div>
);

// Tab Navigation Component
const TabNavigation: React.FC<{
  activeTab: string;
  onTabChange: (
    tab: "pre-judgement" | "dailyReport" | "finalVerdict" | "courtApplication",
  ) => void;
}> = ({ activeTab, onTabChange }) => {
  const { t } = useTranslation();

  const tabs = [
    { id: "pre-judgement", label: t("pre_judgement"), icon: "⚖️" },
    { id: "dailyReport", label: t("daily_report"), icon: "📅" },
    { id: "finalVerdict", label: t("final_verdict"), icon: "🎯" },
    {
      id: "courtApplication",
      label: t("send_to_court", { defaultValue: "Send to Court" }),
      icon: "🏛️",
    },
  ] as const;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
      <div className="flex space-x-1">
        {tabs.map((tab) => (
          <button
            name=""
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
              activeTab === tab.id
                ? "bg-red-50 text-red-700 border border-red-200"
                : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
            }`}
          >
            <span className="text-base">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const AppealReport: React.FC<Props> = ({
  setSelectedAppealDailyReport,
  selectedAppealDailyReport,
  isProsecutor = false,
  isRegistrar = false,
  isCoordinator = false,
  appealData,
  fetchData,
}) => {
  const { t } = useTranslation();
  const [selectedEntity, setSelectedEntity] = useState<any>();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState({
    suspect: false,
    witness: false,
  });
  // Bumped after submit/cancel to remount (reset) the right-column form.
  const [formKey, setFormKey] = useState(0);
  const [activeTab, setActiveTab] = useState<
    "pre-judgement" | "dailyReport" | "finalVerdict" | "courtApplication"
  >("pre-judgement");
  const [openVerdictIndex, setOpenVerdictIndex] = useState<number | null>(null);
  const [openPreJudgementIndexes, setOpenPreJudgementIndexes] = useState<
    number[]
  >([]);
  const [openDailyIndexes, setOpenDailyIndexes] = useState<number[]>([]);
  const [openAppealForm, setOpenAppealForm] = useState(false);
  const [openAppealCassationForm, setOpenAppealCassationForm] = useState(false);
  const [openDefendantCassationForm, setOpenDefendantCassationForm] =
    useState(false);

  // Enhanced buildView function with better styling
  const buildView = (data: any, type: "suspect" | "witness") => {
    return (
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3 flex-1">
          <img
            src={data?.picture || require("../../images/imageVector.png")}
            alt={t("user")}
            className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300"
            onClick={() => {
              setIsImageModalOpen(true);
              setSelectedEntity(data);
            }}
          />
          <div className="flex-1">
            <p className="font-semibold text-gray-900">
              {`${data?.first_name} ${data?.middle_name} ${data?.last_name}`}
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
              {data?.age && (
                <span>
                  {data.age} {t("years")}
                </span>
              )}
              {data?.phone_number && (
                <>
                  <span>•</span>
                  <span>{data.phone_number}</span>
                </>
              )}
              {data?.nationality && (
                <>
                  <span>•</span>
                  <span>{data.nationality}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setIsDetailModalOpen({
              suspect: type === "suspect",
              witness: type === "witness",
            });
            setSelectedEntity(data);
          }}
          className="p-2 text-gray-600 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
          title={t("view_details", { type })}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        </button>
      </div>
    );
  };

  // Render Pre-Judgement Section
  const renderPreJudgement = () => {
    if (
      !appealData?.pre_judgement_entries ||
      appealData.pre_judgement_entries.length === 0
    ) {
      return (
        <EmptyState
          title={t("no_pre_judgement_data")}
          description={t("no_pre_judgement_description")}
        />
      );
    }

    return (
      <div className="space-y-4">
        {appealData.pre_judgement_entries.map((entry, idx) => {
          const open = openPreJudgementIndexes.includes(idx);
          return (
            <ReportCard key={idx}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <span className="text-red-600 text-lg">⚖️</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t("appeal_pre_judgement")}
                    </h3>
                    <p className="text-gray-500 text-sm">
                      {t("decision_date")}:{" "}
                      {entry?.decision_date
                        ? new Date(entry.decision_date).toDateString()
                        : t("n_a")}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setOpenPreJudgementIndexes((prev) =>
                      open ? prev.filter((i) => i !== idx) : [...prev, idx],
                    );
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className={`w-5 h-5 transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>

              {open && (
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t("decision")}
                      </label>
                      <p className="text-gray-900 font-medium mt-1">
                        {entry?.decision
                          ?.replace(/_/g, " ")
                          .replace(/\b\w/g, (c: any) => c.toUpperCase())}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      {t("summary")}
                    </label>
                    <p className="text-gray-900 mt-1 leading-relaxed">
                      {entry?.summary || t("n_a")}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      {t("remarks")}
                    </label>
                    <p className="text-gray-900 mt-1 leading-relaxed">
                      {entry?.remarks || t("n_a")}
                    </p>
                  </div>
                </div>
              )}
            </ReportCard>
          );
        })}
      </div>
    );
  };

  // Render Daily Report Section
  const renderDailyReport = () => {
    if (!appealData?.daily_report || appealData.daily_report.length === 0) {
      return (
        <EmptyState
          title={t("no_daily_report_data")}
          description={t("no_daily_report_description")}
        />
      );
    }

    const sorted = appealData.daily_report.slice().sort((a, b) => {
      const da = a.report_date
        ? new Date(a.report_date).getTime()
        : a.next_trial_appointment
          ? new Date(a.next_trial_appointment).getTime()
          : 0;
      const db = b.report_date
        ? new Date(b.report_date).getTime()
        : b.next_trial_appointment
          ? new Date(b.next_trial_appointment).getTime()
          : 0;
      return da - db; // chronological — oldest first
    });

    return (
      <div className="space-y-3">
        {sorted.map((report, index) => {
          const open = openDailyIndexes.includes(index);
          const selected = selectedAppealDailyReport?.id === report.id;
          return (
            <div
              key={report.id ?? index}
              className={`bg-white rounded-xl border overflow-hidden transition-colors ${
                selected
                  ? "border-red-300 ring-1 ring-red-200"
                  : "border-gray-200"
              }`}
            >
              {/* Accordion header */}
              <button
                type="button"
                onClick={() => {
                  setOpenDailyIndexes((prev) =>
                    prev.includes(index)
                      ? prev.filter((i) => i !== index)
                      : [...prev, index],
                  );
                  setSelectedAppealDailyReport &&
                    setSelectedAppealDailyReport(report);
                }}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 shrink-0 bg-red-50 rounded-lg flex items-center justify-center">
                    <span className="text-red-600">📅</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">
                      {t("daily_report")} #{index + 1}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {report?.report_date
                        ? new Date(report.report_date).toLocaleDateString()
                        : t("n_a")}
                      {report?.next_trial_appointment && (
                        <>
                          {" · "}
                          {t("next_trial_appointment")}:{" "}
                          {new Date(
                            report.next_trial_appointment,
                          ).toLocaleDateString()}
                        </>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {report?.is_final_verdict_given && (
                    <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      ✓ {t("final_verdict")}
                    </span>
                  )}
                  {report?.status && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                      {AppealDailyReportStatus[report.status]}
                    </span>
                  )}
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {/* Accordion body */}
              {open && (
                <div className="px-4 pb-4 pt-3 border-t border-gray-100 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {[
                      {
                        label: t("report_date"),
                        value: report?.report_date
                          ? new Date(report.report_date).toLocaleDateString()
                          : "",
                      },
                      {
                        label: t("next_trial_appointment"),
                        value: report?.next_trial_appointment
                          ? new Date(
                              report.next_trial_appointment,
                            ).toLocaleDateString()
                          : "",
                      },
                      {
                        label: t("appointment_type"),
                        value: report?.appointment_type,
                      },
                      {
                        label: t("appointment_reason"),
                        value: report?.appointment_reason,
                      },
                      {
                        label: t("status"),
                        value: report?.status
                          ? AppealDailyReportStatus[report.status]
                          : "",
                      },
                      {
                        label: t("is_final_verdict_given"),
                        value: report?.is_final_verdict_given
                          ? t("yes", { defaultValue: "Yes" })
                          : t("no", { defaultValue: "No" }),
                      },
                    ].map((f, i) => (
                      <div key={i} className="flex flex-col">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                          {f.label}
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                          {f.value || t("n_a")}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                      {t("daily_report_summary")}
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-line">
                      {report?.dailyreport_summary || t("n_a")}
                    </p>
                  </div>

                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                      {t("court_order_next_appointment")}
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-line">
                      {report?.court_order_for_next_appointment || t("n_a")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render Final Verdict Section
  const renderFinalVerdict = () => {
    if (
      !appealData?.appeal_final_verdict ||
      appealData.appeal_final_verdict.length === 0
    ) {
      return (
        <EmptyState
          title={t("no_final_verdict_data")}
          description={t("no_final_verdict_description")}
        />
      );
    }

    const decisionColor = (type?: string) => {
      switch (type) {
        case "UPHELD":
          return "bg-blue-50 text-blue-700 border-blue-200";
        case "OVERTURNED":
          return "bg-red-50 text-red-700 border-red-200";
        case "MODIFIED":
          return "bg-amber-50 text-amber-700 border-amber-200";
        case "REMANDED":
          return "bg-purple-50 text-purple-700 border-purple-200";
        default:
          return "bg-emerald-50 text-emerald-700 border-emerald-200";
      }
    };

    return (
      <div className="space-y-3">
        {appealData.appeal_final_verdict.map((report, index) => {
          const open = openVerdictIndex === index;
          const decisionType = report?.amended_court_decision_type;
          const showAmended =
            !!decisionType && !["UPHELD", "REMANDED"].includes(decisionType);
          const defendantNames = (report?.defendants || [])
            .map((d: any) =>
              [
                d?.defendant_info?.first_name,
                d?.defendant_info?.middle_name,
                d?.defendant_info?.last_name,
              ]
                .filter(Boolean)
                .join(" "),
            )
            .filter(Boolean)
            .join(", ");
          const defendantLabel = report?.defendant_name || defendantNames;
          return (
            <div
              key={index}
              className="bg-white rounded-xl border-2 border-emerald-200 overflow-hidden"
            >
              {/* Accordion header */}
              <button
                type="button"
                onClick={() => setOpenVerdictIndex(open ? null : index)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-emerald-50/40 text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 shrink-0 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <span className="text-emerald-600">🎯</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">
                      {t("appeal_final_verdict")} #{index + 1}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {defendantLabel || t("n_a")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {decisionType && (
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${decisionColor(
                        decisionType,
                      )}`}
                    >
                      {(AmmendedCourtDecisionType as any)[decisionType] ||
                        decisionType}
                    </span>
                  )}
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {/* Accordion body */}
              {open && (
                <div className="px-4 pb-4 pt-3 border-t border-gray-100 space-y-3">
                  {report?.defendants && report.defendants.length > 0 && (
                    <div className="space-y-2">
                      {report.defendants.map((def, idx) => (
                        <div key={idx}>
                          {buildView(def?.defendant_info, "suspect")}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {[
                      { label: t("defendant"), value: defendantLabel },
                      {
                        label: t("amended_decision_type"),
                        value: decisionType
                          ? (AmmendedCourtDecisionType as any)[decisionType] ||
                            decisionType
                          : "",
                      },
                      ...(showAmended
                        ? [
                            {
                              label: t("amended_decision"),
                              value: report?.amended_court_decision,
                            },
                            {
                              label: t("amended_decision_amount"),
                              value: report?.amended_court_decision_amount,
                            },
                          ]
                        : []),
                    ].map((f, i) => (
                      <div key={i} className="flex flex-col">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                          {f.label}
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                          {f.value || t("n_a")}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                      {t("court_decision_summary")}
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-line">
                      {report?.court_decision_summary || t("n_a")}
                    </p>
                  </div>

                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                      {t("prosecutor_feedback")}
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-line">
                      {report?.prosecutor_feedback_on_decision || t("n_a")}
                    </p>
                  </div>

                  {/* Final court-decision file — uploaded after creation */}
                  <FinalVerdictDecisionUpload
                    verdictId={report?.id}
                    documents={(report as any)?.documents}
                    canUpload={!!isProsecutor}
                    onUploaded={fetchData}
                  />

                  <div className="flex flex-wrap justify-end gap-3 pt-1">
                    {isProsecutor && (
                      <button
                        onClick={() => setOpenAppealForm(true)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                      >
                        {t("request_appeal")}
                      </button>
                    )}
                    {isProsecutor && (
                      <button
                        onClick={() => setOpenAppealCassationForm(true)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
                      >
                        {t("request_cassation")}
                      </button>
                    )}
                    {isRegistrar && (
                      <button
                        onClick={() => setOpenDefendantCassationForm(true)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
                      >
                        {t("request_defendant_cassation", {
                          defaultValue: "Request Defendant Cassation",
                        })}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Litigation is finished once every defendant has a final verdict — then we
  // stop allowing new daily reports / final verdicts.
  const verdictDefendantIds = new Set<string>();
  (appealData?.appeal_final_verdict || []).forEach((v: any) => {
    const ids = Array.isArray(v?.defendant_ids)
      ? v.defendant_ids
      : (v?.defendants || []).map((d: any) => d?.id);
    (ids || []).forEach((id: any) =>
      verdictDefendantIds.add(String(typeof id === "object" ? id?.id : id)),
    );
  });
  const appealDefendantIds = (appealData?.defendants || []).map((d: any) =>
    String(d?.id),
  );
  const allFinalVerdictsSubmitted =
    appealDefendantIds.length > 0 &&
    appealDefendantIds.every((id) => verdictDefendantIds.has(id));

  const finishedNotice = (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center">
      <div className="text-2xl mb-1">✅</div>
      <p className="text-sm font-semibold text-emerald-800">
        {t("all_final_verdicts_submitted", {
          defaultValue: "All final verdicts have been submitted.",
        })}
      </p>
      <p className="text-xs text-emerald-700/80 mt-1">
        {t("litigation_finished_note", {
          defaultValue:
            "Litigation is finished — no further daily reports or verdicts can be added.",
        })}
      </p>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header with Tabs */}
      <div className="mb-6">
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Two-column: submitted items (left) + submit / details (right) */}
      <div className="flex-1 overflow-y-auto pr-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Left — submitted items */}
          <div>
            {activeTab === "pre-judgement" && renderPreJudgement()}
            {activeTab === "dailyReport" && renderDailyReport()}
            {activeTab === "finalVerdict" && renderFinalVerdict()}
            {activeTab === "courtApplication" && appealData?.id && (
              <AppealApplicationTab
                mode="list"
                appealRequestId={String(appealData.id)}
                isProsecutor={isProsecutor}
                isCoordinator={isCoordinator}
                isRegistrar={isRegistrar}
                refreshSignal={formKey}
                onChanged={() => setFormKey((k) => k + 1)}
              />
            )}
          </div>

          {/* Right — details panel for non-prosecutor roles. */}
          {!isProsecutor && appealData && (
            <div className="lg:sticky lg:top-0">
              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-base">ℹ️</span>
                  <h4 className="text-sm font-semibold text-gray-700">
                    {t("details", { defaultValue: "Details" })}
                  </h4>
                </div>
                <p className="text-sm text-gray-500">
                  {activeTab === "courtApplication"
                    ? t("court_application_role_hint", {
                        defaultValue:
                          "Open an application on the left to review it and take action — forward, approve, send to court, or attach the court response.",
                      })
                    : t("report_view_hint", {
                        defaultValue:
                          "Open an item on the left to view its full details.",
                      })}
                </p>
              </div>
            </div>
          )}

          {/* Right — submit form (prosecutor only) */}
          {isProsecutor && appealData && (
            <div key={formKey} className="lg:sticky lg:top-0">
              {activeTab === "courtApplication" && appealData?.id && (
                <AppealApplicationTab
                  mode="form"
                  appealRequestId={String(appealData.id)}
                  isProsecutor={isProsecutor}
                  onChanged={() => setFormKey((k) => k + 1)}
                />
              )}
              {activeTab === "pre-judgement" && (
                <AppealPreJudgmentForm
                  inline
                  appealData={appealData}
                  onClose={(reload) => {
                    if (reload) fetchData();
                    setFormKey((k) => k + 1);
                  }}
                />
              )}
              {activeTab === "dailyReport" &&
                (allFinalVerdictsSubmitted ? (
                  finishedNotice
                ) : (
                  <AppealAddDailyReport
                    inline
                    appealData={appealData}
                    onClose={(reload) => {
                      if (reload) fetchData();
                      setFormKey((k) => k + 1);
                    }}
                  />
                ))}
              {activeTab === "finalVerdict" &&
                (allFinalVerdictsSubmitted ? (
                  finishedNotice
                ) : (
                  <AppealAddFinalVerdict
                    inline
                    appealData={appealData}
                    onClose={(reload) => {
                      if (reload) fetchData();
                      setFormKey((k) => k + 1);
                    }}
                  />
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={
            selectedEntity?.picture || require("../../images/imageVector.png")
          }
          label={`${selectedEntity?.first_name} ${selectedEntity?.last_name}`}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}

      {isDetailModalOpen.suspect && (
        <SuspectDetailInfo
          suspect={selectedEntity}
          isOpen={isDetailModalOpen.suspect}
          isDefendant={true}
          onClose={() =>
            setIsDetailModalOpen({ suspect: false, witness: false })
          }
        />
      )}


      {openAppealForm && appealData && (
        <AppealRequestForm
          appeal={appealData}
          appealType={
            appealData?.appeal_type as "PRE_JUDGEMENT" | "FINAL_VERDICT"
          }
          onClose={() => setOpenAppealForm(false)}
        />
      )}
      {openAppealCassationForm && appealData && (
        <AppealRequestForm
          appeal={appealData}
          appealType={
            appealData?.appeal_type as "PRE_JUDGEMENT" | "FINAL_VERDICT"
          }
          isCassation={true}
          onClose={() => setOpenAppealCassationForm(false)}
        />
      )}
      {openDefendantCassationForm && appealData && (
        <DefendantAppealRequestForm
          isCassation
          originatingProsecutorAppealId={appealData?.id?.toString()}
          prefillAppeal={
            {
              // Map the prosecutor appeal onto the defendant-appeal shape.
              case: (appealData as any)?.case,
              count: (appealData as any)?.count,
              defendants: (appealData as any)?.defendants,
              crime_type: (appealData as any)?.crime_type,
              prosecutor_registration_number: (appealData as any)
                ?.prosecutor_registration_number,
              bail_right: (appealData as any)?.bail_right,
              higher_court_type: (appealData as any)?.higher_court_type,
              higher_court_level: (appealData as any)?.higher_court_level,
              higher_court_hearing_date: (appealData as any)
                ?.higher_court_hearing_date,
              court_zone: (appealData as any)?.higher_court_zone,
              court_woreda: (appealData as any)?.higher_court_woreda,
              court_appeal_number: (appealData as any)?.court_appeal_number,
              opening_date: (appealData as any)?.opening_date,
              first_hearidng_date: (appealData as any)?.first_hearidng_date,
            } as any
          }
          onClose={() => setOpenDefendantCassationForm(false)}
        />
      )}
    </div>
  );
};

export default AppealReport;
